/**
 * The own background photo on this device (docs/partial/appearance-and-themes-spec.md 6.3, 6.5).
 * Signed URLs run out and need a connection, so the picture is kept in the Cache Storage under
 * one fixed key and replaced when the profile names another file. The signed URL itself is never
 * stored.
 */
export const OWN_PHOTO_CACHE = 'appearance';
export const OWN_PHOTO_KEY = '/__appearance/background';
const PATH_KEY = 'rm.appearance.backgroundPath';

/** The part of a signed URL that names the file – the signature changes with every response. */
const pathOf = (url: string) => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url;
  }
};

/** The photo as a blob: from the cache, refreshed when the profile names another file. */
export async function loadOwnPhoto(url: string | null): Promise<Blob | null> {
  if (typeof caches === 'undefined') return null;
  const cache = await caches.open(OWN_PHOTO_CACHE);
  let response = await cache.match(OWN_PHOTO_KEY);
  const known = localStorage.getItem(PATH_KEY);
  if (url && (!response || known !== pathOf(url)) && navigator.onLine) {
    const fresh = await fetch(url);
    if (fresh.ok) {
      await cache.put(OWN_PHOTO_KEY, fresh.clone());
      localStorage.setItem(PATH_KEY, pathOf(url));
      response = fresh;
    }
  }
  return response ? response.blob() : null;
}

/** Forgets the cached photo (removed, or another user signs in on this device). */
export async function forgetOwnPhoto(): Promise<void> {
  try {
    localStorage.removeItem(PATH_KEY);
    if (typeof caches !== 'undefined')
      await (await caches.open(OWN_PHOTO_CACHE)).delete(OWN_PHOTO_KEY);
  } catch {
    // nothing cached
  }
}
